import React, { useCallback, useMemo, useState, useContext } from 'react'
import useGroomingVisit from '../../../../../hooks/useGroomingVisit'
import OptionsMenu from '../../../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../../../components/SectionFormContainer'
import InfoDialog from '../../../components/InfoDialog'
import SubOptionsDialog from '../../../components/SubOptionsDialog'
import { capitilizeMultipleWords, formatNumsToDollars } from '../../../../../utils'
import GroomingAppointmentWizardContext from '../GroomingAppointmentWizardContext'

const DEFAULT_POPUP_CONTENT = { title: '', description: '' }
const DEFAULT_SUBOPTIONS_CONTENT = { title: '', description: '', options: [] }

const AddOnSelection = (props) => {
	const {
		wizardContextProvider: { grooming_appointment, setWizardAttributeAndValues }
	} = useContext(GroomingAppointmentWizardContext)
	const { visit_attr, title = '', subtext = '' } = props
	const { loading, facility } = useGroomingVisit()

	const [infoPopupOpen, setInfoPopupOpen] = useState(false)
	const [subOptionsDialogOpen, setSubOptionsDialogOpen] = useState(false)
	const [currentPopupContent, setCurrentPopupContent] = useState(DEFAULT_POPUP_CONTENT)
	const [currentSubOptions, setCurrentSubOptions] = useState(DEFAULT_SUBOPTIONS_CONTENT)

	const handleSelection = useCallback(
		(addon) => {
			const newValues = { ...grooming_appointment }
			let newAddOnArray = []
			const { name } = addon
			const hasAddon = grooming_appointment[visit_attr].findIndex((a) => a.name === name)
			if (hasAddon > -1) {
				newAddOnArray = newValues[visit_attr].filter((a) => a.name !== name)
			} else {
				newAddOnArray = [...newValues[visit_attr], addon]
			}
			newValues[visit_attr] = newAddOnArray
			setWizardAttributeAndValues(newValues)
		},
		[grooming_appointment, setWizardAttributeAndValues, visit_attr]
	)

	const handleInfoPopupClose = useCallback(() => {
		setInfoPopupOpen(false)
		setTimeout(() => {
			setCurrentPopupContent(DEFAULT_POPUP_CONTENT)
		}, 400)
	}, [setInfoPopupOpen, setCurrentPopupContent])

	const handleSubOptionsDialogClose = useCallback(() => {
		setSubOptionsDialogOpen(false)
		setTimeout(() => {
			setCurrentSubOptions(DEFAULT_SUBOPTIONS_CONTENT)
		}, 400)
	}, [setCurrentSubOptions, setSubOptionsDialogOpen])

	const handleSubOptionSelection = useCallback(
		({ addon_choices, parentAddOn }) => {
			setSubOptionsDialogOpen(true)

			const {
				price: basePrice,
				name: parentAddOnName,
				description: parentAddOnDescription,
				addon_choice_id
			} = parentAddOn

			const addOnOptions = addon_choices.map((addon, i) => {
				const { name, upcharge, _id, id } = addon
				const cost = basePrice + upcharge
				const option = {
					title: capitilizeMultipleWords(name, ' '),
					description: formatNumsToDollars(cost),
					id: i,
					selected: addon_choice_id === id,
					useTemporarySelectionVisualFeedback: true,
					onSelected: () => {
						handleSelection({ ...parentAddOn, price: cost, addon_choice_id: id })
						handleSubOptionsDialogClose()
					}
				}
				return option
			})

			setCurrentSubOptions({
				title: parentAddOnName,
				description: parentAddOnDescription,
				options: addOnOptions
			})
		},
		[handleSelection, handleSubOptionsDialogClose, setSubOptionsDialogOpen, setCurrentSubOptions]
	)

	const handleInfoButtonClicked = useCallback(
		(content) => {
			setInfoPopupOpen(true)
			setCurrentPopupContent(content)
		},
		[setInfoPopupOpen]
	)

	const options = useMemo(() => {
		if (
			!facility ||
			!facility.services ||
			!facility.services.grooming ||
			!facility.services.grooming.services
		) {
			return []
		}

		const { addOnTypes } = facility || []

		const opts = []

		addOnTypes.forEach((addon, i) => {
			const {
				active,
				grooming_service,
				name,
				description,
				addon_choices = [],
				price,
				min_price,
				max_price
			} = addon
			if (grooming_service && active) {
				const hasSuboptions = addon_choices.length > 0
				const selected = grooming_appointment[visit_attr].find((addon) => addon.name === name)
				const title = capitilizeMultipleWords(name, ' ')

				let descriptionFormatted = formatNumsToDollars(price)

				if (max_price > min_price) {
					descriptionFormatted = `${formatNumsToDollars(min_price)} - ${formatNumsToDollars(max_price)}`
				}

				// if suboptions, show base and highest price, if there is a higher price than the base.
				if (hasSuboptions) {
					let largestAmount = price
					addon_choices.forEach(({ upcharge }) => {
						const total = price + upcharge
						if (total > largestAmount) {
							largestAmount = total
						}
					})
					if (largestAmount > price) {
						descriptionFormatted += ` - ${formatNumsToDollars(largestAmount)} `
					}
				}

				// if option is selected and has a suboption selected, then show the suboption name and price
				if (selected && selected.addon_choice_id && selected.addon_choice_id > '') {
					const suboption = addon_choices.find(({ id }) => id === selected.addon_choice_id)
					if (suboption) {
						descriptionFormatted = `${suboption.name} - ${formatNumsToDollars(
							price + suboption.upcharge
						)}`
					}
				}

				const option = {
					title,
					description: descriptionFormatted,
					id: i,
					disabled: loading,
					selected: selected,
					onSelected: () => {
						if (!hasSuboptions || selected) {
							handleSelection(addon)
						} else {
							handleSubOptionSelection({ addon_choices, parentAddOn: addon })
						}
					},
					onInfoButtonClicked: () => handleInfoButtonClicked({ title, description })
				}
				opts.push(option)
			}
		})

		return opts
	}, [facility, grooming_appointment, visit_attr, handleSelection, handleInfoButtonClicked, loading])

	return (
		<>
			<InfoDialog
				onClose={handleInfoPopupClose}
				open={infoPopupOpen}
				title={currentPopupContent.title}
				description={currentPopupContent.description}
			/>
			<SubOptionsDialog
				title={currentSubOptions.title}
				description={currentSubOptions.description}
				onClose={handleSubOptionsDialogClose}
				open={subOptionsDialogOpen}
				numberOfOptions={currentSubOptions.options?.length}>
				<OptionsMenu options={currentSubOptions.options} />
			</SubOptionsDialog>
			<SectionFormContainer title={title} subtext={subtext}>
				<OptionsMenu options={options} />
			</SectionFormContainer>
		</>
	)
}

export default AddOnSelection
